import { useState, useCallback, useEffect, useRef } from "react";
import { createFileRoute, Link, Outlet, useMatch, useNavigate } from "@tanstack/react-router";
import {
  Clock,
  RefreshCw,
  CheckCircle,
  XCircle,
  Upload,
  Eye,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";
import { formatDateTime } from "@/lib/dateFormat";
import { toast } from "sonner";
import { ragApi, type Batch, type BatchStatus } from "@/services/api/rag";

export const Route = createFileRoute("/portal/knowledgebase-file/batches")({
  component: BatchesLayout,
});

function BatchesLayout() {
  const childMatch = useMatch({
    from: "/portal/knowledgebase-file/batches/$id",
    shouldThrow: false,
  });

  if (childMatch) {
    return <Outlet />;
  }

  return <BatchesListPage />;
}

function BatchesListPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const canCreateKbFile = can("create", "kb-file");
  const canEditKbFile = can("edit", "kb-file");
  const organisationId = selectedOrganisation?.organisationId || "";
  const navigate = useNavigate();

  // Search, filter & sort state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<BatchStatus | "all">("all");
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Polling ref
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const getSortDirection = (column: string): "asc" | "desc" | null => {
    return sortBy === column ? sortOrder : null;
  };

  // Server-side pagination
  const fetchBatches = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<Batch>> => {
      if (!token || !organisationId)
        return { data: [], totalCount: 0, page, pageSize };
      const response = await ragApi.listBatches(token, {
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy as "created_at" | "started_at" | "completed_at" | "status" | "batch_name",
        sort_order: sortOrder,
      });
      return {
        data: response.batches,
        totalCount: response.total,
        page,
        pageSize,
      };
    },
    [token, organisationId, searchTerm, statusFilter, sortBy, sortOrder],
  );

  const {
    data: batches,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<Batch>({
    fetchFn: fetchBatches,
    dependencies: [token, organisationId, searchTerm, statusFilter, sortBy, sortOrder],
  });

  // Smart polling: only poll when batches are actively processing
  useEffect(() => {
    const hasActive = batches.some(
      (b) => b.status === "pending" || b.status === "processing",
    );
    if (hasActive) {
      pollIntervalRef.current = setInterval(() => {
        refresh();
      }, 5000);
      return () => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      };
    } else if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, [batches, refresh]);

  const handleRetryFailed = async (batchId: string) => {
    if (!token) return;
    try {
      const response = await ragApi.retryFailedDocuments(token, batchId);
      toast.success("Retry queued", {
        description: `${response.retried_count} document${response.retried_count !== 1 ? "s" : ""} queued for retry`,
      });
      refresh();
    } catch (error) {
      console.error("Failed to retry documents:", error);
      toast.error("Retry failed", {
        description: "Could not retry failed documents",
      });
    }
  };

  const getStatusBadge = (status: BatchStatus) => {
    switch (status) {
      case "completed":
        return (
          <Badge variant="success">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completed
          </Badge>
        );
      case "processing":
        return (
          <Badge variant="default">
            <Clock className="mr-1 h-3 w-3" />
            Processing
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline">
            <XCircle className="mr-1 h-3 w-3" />
            Cancelled
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDuration = (
    startDate: string | null,
    endDate: string | null,
  ): string => {
    if (!startDate) return "-";
    const start = new Date(startDate).getTime();
    const end = endDate ? new Date(endDate).getTime() : Date.now();
    const duration = Math.floor((end - start) / 1000);

    if (duration < 60) return `${duration}s`;
    if (duration < 3600)
      return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900">
            Processing History
          </h1>
          <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
            Track document processing batches
          </p>
        </div>
        {canCreateKbFile && (
          <Link to="/portal/knowledgebase-file/upload">
            <Button className="rounded-full">
              <Upload className="h-4 w-4 mr-2" />
              Upload Documents
            </Button>
          </Link>
        )}
      </div>

      {/* Content card */}
      <div className="rounded-3xl border border-slate-100 bg-white p-3 md:p-6 shadow-sm space-y-4 md:space-y-6">
        {/* Controls bar */}
        <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search by batch name..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as BatchStatus | "all")
            }
          >
            <SelectTrigger className="w-full md:w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Polling indicator */}
        {batches.some(
          (b) => b.status === "pending" || b.status === "processing",
        ) && (
          <p className="text-xs text-muted-foreground">
            Auto-refreshing every 5 seconds while batches are active
          </p>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : batches.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm || statusFilter !== "all"
                ? "No batches match your filters"
                : "No batches yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your filters."
                : "Upload documents to create your first ingestion batch."}
            </p>
            {!searchTerm && statusFilter === "all" && canCreateKbFile && (
              <Link to="/portal/knowledgebase-file/upload">
                <Button>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Documents
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("batch_name")}
                  onSort={() => handleSort("batch_name")}
                >
                  Batch Name
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("status")}
                  onSort={() => handleSort("status")}
                >
                  Status
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="hidden md:table-cell"
                >
                  Progress
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="hidden md:table-cell"
                >
                  Documents
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="hidden lg:table-cell"
                >
                  Duration
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("created_at")}
                  onSort={() => handleSort("created_at")}
                  className="hidden lg:table-cell"
                >
                  Created
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="w-[50px]"
                >
                  {" "}
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((batch) => (
                <TableRow key={batch.batch_id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      {batch.batch_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(batch.status as BatchStatus)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Progress
                          value={batch.progress.percentage}
                          className="h-2 w-24"
                        />
                        <span className="text-xs text-muted-foreground">
                          {Math.round(batch.progress.percentage)}%
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {batch.progress.completed}/{batch.progress.total}{" "}
                        completed
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="text-sm">
                      <div>{batch.progress.total} total</div>
                      {batch.progress.failed > 0 && (
                        <div className="text-xs text-red-500">
                          {batch.progress.failed} failed
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {formatDuration(batch.started_at, batch.completed_at)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {formatDateTime(batch.created_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            navigate({
                              to: "/portal/knowledgebase-file/batches/$id",
                              params: { id: batch.batch_id },
                            })
                          }
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {canEditKbFile && (batch.status === "failed" || batch.progress.failed > 0) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleRetryFailed(batch.batch_id)}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Retry Failed ({batch.progress.failed})
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.totalCount > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              pageSizeOptions={pageSizeOptions}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={goToPage}
              onPageSizeChange={setPageSize}
              onFirstPage={firstPage}
              onLastPage={lastPage}
              onPrevPage={prevPage}
              onNextPage={nextPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
